import { CLOSING_MSG, IMPORT_MSG, NUMBERING_MSG, RECURRING_MSG, INVOICE_BULK_MSG, INVOICE_PAY_TAB_LABEL, INVOICE_STATE_LABEL, overdueText, type Invoice, type InvoiceList, type InvoiceSummary } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { ListHead } from "../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../components/ListTabs.jsx";
import { usePageName } from "../components/BackButton.jsx";
import { ErrorText, Help, type ToastFn } from "../components/ui.jsx";
import { useModule } from "../core/slots.jsx";
import { useLabels } from "../labels.js";
import { md, todayJst, yen } from "../lib/num.js";
import { usePcOnly } from "../lib/pcOnly.js";
import { InvoiceBulkBand } from "./InvoiceBulk.jsx";

const ST: Record<InvoiceSummary["status"], string> = { draft: "下書き", issued: "出した", sent: "送った", paid: "入金済み", lost: "—", voided: "取り消し" };
const FILTERS = [
  ["all", "すべて"],
  ["draft", "下書き"],
  ["issued", "出したもの"],
] as const;
/** 入金のタブ（E1、営業力 docs/08 決めごと9）。API が payCounts を返す会社（sales）だけ出す */
const PAY_FILTERS = [
  ["unpaid", INVOICE_PAY_TAB_LABEL.unpaid],
  ["overdue", INVOICE_PAY_TAB_LABEL.overdue],
  ["paid", INVOICE_PAY_TAB_LABEL.paid],
] as const;
type Filter = (typeof FILTERS)[number][0] | (typeof PAY_FILTERS)[number][0] | "recurring";

/**
 * 受注した見積から請求書を起こして、その紙へ移る（縦串5）。
 * 下書きがもうあれば（409 INVOICE_DRAFT_EXISTS）そちらをひらく。失敗の文は toast へ
 */
export function useCreateInvoice(toast: ToastFn) {
  const nav = useNavigate();
  const qc = useQueryClient();
  /** 見積から（一括）、または分割の予定の回から（042）起こす */
  return async (fromQuoteId: string, fromInstallmentId?: string) => {
    try {
      const inv = await api<Invoice>("POST", "/invoices", fromInstallmentId ? { fromInstallmentId } : { fromQuoteId });
      qc.setQueryData(["invoice", inv.id], inv);
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void qc.invalidateQueries({ queryKey: ["estimates"] });
      void qc.invalidateQueries({ queryKey: ["estimate", fromQuoteId] });
      void qc.invalidateQueries({ queryKey: ["group"] });
      void qc.invalidateQueries({ queryKey: ["home"] });
      // 起こした直後の一言は通知（#171。前は下書きの帯「一括の請求書です。見積 … の明細を写しました。」）
      const i = inv.installment;
      const notice = i
        ? `${i.count}回のうち${i.seq}回目（${i.label}）の請求書を作成しました。`
        : `${inv.project.quoteNo ? `見積 ${inv.project.quoteNo} の明細を写しました。` : "見積の明細を写しました。"}${inv.project.invoiceCount > 0 ? `この見積の ${inv.project.invoiceCount + 1} 枚目の請求書です。` : ""}`;
      nav(`/invoices/${inv.id}`, { state: { fresh: true, notice } });
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVOICE_DRAFT_EXISTS" && typeof e.problem.invoiceId === "string") {
        toast(messageOf(e), undefined, "error");
        nav(`/invoices/${e.problem.invoiceId}`);
        return;
      }
      toast(messageOf(e), undefined, "error");
    }
  };
}

/** 請求書の一覧。押すとその紙が開く。新しく起こすのは見積の一覧（受注の行）から */
export function InvoicesPage() {
  const L = useLabels();
  usePageName("請求書");
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState<Filter>(() => {
    const q = sp.get("tab");
    return q === "unpaid" || q === "overdue" || q === "paid" || q === "draft" || q === "issued" || q === "recurring" ? q : "all";
  });
  const list = useQuery({ queryKey: ["invoices"], queryFn: () => api<InvoiceList>("GET", "/invoices") });
  const all = list.data?.items ?? [];
  const pays = !!list.data?.payCounts;
  const thisMonth = todayJst().slice(0, 7);
  const rcOn = !!list.data?.recurringAvailable;
  const pick = (k: Filter) =>
    k === "all" ? all
    : k === "draft" ? all.filter((x) => x.status === "draft")
    : k === "issued" ? all.filter((x) => x.status !== "draft")
    : k === "unpaid" ? all.filter((x) => (x.balance ?? 0) > 0)
    : k === "overdue" ? all.filter((x) => x.payState === "overdue")
    // 今月の定期請求（#268）：請求月が今月の定期請求の請求書（下書き＋発行済）
    : k === "recurring" ? all.filter((x) => x.recurringMonth === thisMonth)
    : all.filter((x) => x.payState === "paid");
  const items = pick(f);
  const tabs: ReadonlyArray<readonly [Filter, string]> = [...FILTERS, ...(pays ? PAY_FILTERS : []), ...(rcOn ? ([["recurring", RECURRING_MSG.invoiceTab]] as const) : [])];
  // まとめて発行・PDF・メール（#267 3c）：sales の会社の PC だけ。取り消し（voided）は選べない。選んだものはタブを変えても残す
  const bulk = useModule("sales") && !usePcOnly().phone;
  const [picked, setPicked] = useState<string[]>([]);
  const selectable = (x: InvoiceSummary) => x.status !== "voided";
  const pickedRows = bulk ? all.filter((x) => picked.includes(x.id) && selectable(x)) : [];
  const shown = items.filter(selectable);
  const allShown = shown.length > 0 && shown.every((x) => picked.includes(x.id));
  const cols = (pays ? 8 : 7) + (bulk ? 1 : 0);
  return (
    <ListHead
      title={
        <>
          請求書
          <Help testId="help-invoices" label="請求書の一覧の説明" text="請求書は、受注した見積の行の「請求書作成」から起こします。" />
        </>
      }
    >
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      {/* 絞り込みはタブ（9/24、幅はそろえる）。#241：タブの段は表のカードの中の上辺（請求書は作成のボタンを足さない） */}
      <div className="lcard">
      <ListTabs
        id="il"
        label="請求書を状態で絞る"
        tabs={tabs.map(([k, label]) => ({ key: k as Filter, label, count: list.data ? pick(k as Filter).length : "…" }))}
        value={f}
        onChange={setF}
        // 締め請求書（#267）：締めの{customer}・{project}か締め請求書のある会社だけ入口
        // 定期請求（#268）：定期の{project}か定期の請求書のある会社だけ入口
        create={
          list.data?.closingAvailable || rcOn ? (
            <span className="row il-entries">
              {rcOn ? (
                <Link className="btn tonal" to="/recurring" data-testid="invoices-recurring">
                  {RECURRING_MSG.listEntry}
                </Link>
              ) : null}
              {list.data?.closingAvailable ? (
                <Link className="btn tonal" to="/closing" data-testid="invoices-closing">
                  {CLOSING_MSG.title}
                </Link>
              ) : null}
            </span>
          ) : undefined
        }
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel("il", f)}>
        {pickedRows.length ? <InvoiceBulkBand picked={pickedRows} onClear={() => setPicked([])} /> : null}
        <table className="list inv-list" data-testid="invoice-list">
          <thead>
            <tr>
              {bulk ? (
                <th className="pick">
                  {shown.length ? (
                    <input
                      type="checkbox"
                      aria-label={INVOICE_BULK_MSG.pickAll}
                      checked={allShown}
                      onChange={(e) => setPicked((p) => (e.target.checked ? [...new Set([...p, ...shown.map((x) => x.id)])] : p.filter((id) => !shown.some((x) => x.id === id))))}
                      data-testid="invoice-pick-all"
                    />
                  ) : null}
                </th>
              ) : null}
              <th>{L.customer}</th>
              <th>{L.subject === "件名" ? "件名" : `件名（${L.subject}）`}</th>
              <th>請求日</th>
              <th>お支払期限</th>
              <th className="amt">金額（税込）</th>
              {pays ? <th className="amt">残</th> : null}
              <th>いまの状態</th>
              <th>番号</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} className="r" data-testid="invoice-row" onClick={() => nav(`/invoices/${x.id}`)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && nav(`/invoices/${x.id}`)}>
                {bulk ? (
                  <td className="pick" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {selectable(x) ? (
                      <input
                        type="checkbox"
                        aria-label={INVOICE_BULK_MSG.pick(x.clientName)}
                        checked={picked.includes(x.id)}
                        onChange={(e) => setPicked((p) => (e.target.checked ? [...p, x.id] : p.filter((y) => y !== x.id)))}
                        data-testid="invoice-pick"
                      />
                    ) : null}
                  </td>
                ) : null}
                <td>
                  <b>{x.clientName}</b>
                </td>
                <td>
                  {x.subject || <span className="muted">（件名はまだ）</span>}
                  {x.recurringMonth ? (
                    <div className="muted" style={{ fontSize: 14 }} data-testid="invoice-recurring-month">
                      {RECURRING_MSG.title} {Number(x.recurringMonth.slice(5, 7))}月分
                    </div>
                  ) : null}
                  {x.closingPeriodEnd ? (
                    <div className="muted" style={{ fontSize: 14 }} data-testid="invoice-closing-month">
                      締め {Number(x.closingPeriodEnd.slice(5, 7))}月分
                    </div>
                  ) : null}
                  {x.quoteNo ? (
                    <div className="muted" style={{ fontSize: 14 }}>
                      見積 {x.quoteNo} から{x.installment ? `　分割 ${x.installment.seq}回目／${x.installment.count}回${x.installment.label ? ` ${x.installment.label}` : ""}` : "　一括"}
                    </div>
                  ) : null}
                </td>
                <td className="num">{md(x.issueDate)}</td>
                <td className="num">{x.dueDate ? md(x.dueDate) : <span className="muted">—</span>}</td>
                <td className="amt num">{x.total ? `${yen(x.total)} 円` : <span className="muted">—</span>}</td>
                {pays ? <td className="amt num" data-testid="invoice-balance">{x.balance != null ? `${yen(x.balance)} 円` : <span className="muted">—</span>}</td> : null}
                <td data-testid="invoice-state">
                  {x.status === "draft" ? <span className="chip">下書き</span> : x.payState ? INVOICE_STATE_LABEL[x.payState] : ST[x.status]}
                  {x.payState === "overdue" && x.overdueDays ? <div className="shu" style={{ fontSize: 14 }}>{overdueText(x.overdueDays)}</div> : null}
                </td>
                <td className="num">
                  {x.documentNo ?? <span className="muted">未発行</span>}
                  {x.imported ? (
                    <span className="il-mgmt" data-testid="invoice-imported-mark">
                      {IMPORT_MSG.imported}
                    </span>
                  ) : x.managementNo ? (
                    <span className="il-mgmt" data-testid="invoice-management-no">
                      {NUMBERING_MSG.managementShort(x.managementNo)}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={cols}>{all.length ? `「${tabs.find((x) => x[0] === f)?.[1] ?? ""}」の請求書はいまありません。` : "まだ請求書はありません。見積書の一覧で「受注」を押し、「請求書作成」から起こせます。"}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      </div>
    </ListHead>
  );
}
